import { createFileRoute, Link } from '@tanstack/react-router'
import { Calendar, FilterIcon, Tag } from 'lucide-react'
import React, { useMemo, useState } from 'react'

import { ArticleStatusBadge } from '@/components/articles/ArticleStatusBadge'
import { PageHeader } from '@/components/PageHeader'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { orpc } from '@/lib/orpc/client'
import { keepPreviousData, useQuery } from '@tanstack/react-query'

export const Route = createFileRoute('/_dashboard/articles/')({
  component: ArticlesPage,
})

function getFilter(filter: 'all' | 'published' | 'draft' | 'scheduled') {
  if (filter === 'all') {
    return undefined
  }
  switch (filter) {
    case 'published':
      return 'published'
    case 'draft':
      return 'draft'
    case 'scheduled':
      return 'scheduled-for-publishing'
  }
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function ArticlesPage() {
  const [filter, setFilter] = useState<
    'all' | 'published' | 'draft' | 'scheduled'
  >('all')
  // const [searchTerm, setSearchTerm] = useState('')

  const { data: articleData, isPending } = useQuery(
    orpc.articles.getListOfArticles.queryOptions({
      input: { status: getFilter(filter) },
      placeholderData: keepPreviousData,
      refetchInterval: 10_000, // TODO: improve this with websockets. I'm just too lazy to do it right now.
    }),
  )

  const articles = useMemo(
    () =>
      (articleData ?? []).filter(
        (article) =>
          article.status === 'draft' ||
          article.status === 'scheduled-for-publishing' ||
          article.status === 'writing' ||
          article.status === 'published' ||
          article.status === 'writing-failed',
      ),
    [articleData],
  )

  return (
    <div className="m-4 md:m-8">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <PageHeader
          title="Your Articles"
          description="Manage your articles that are being created by AI."
          className="mb-8"
        />

        {/* Stats */}
        {/* <div className="grid grid-cols-1 gap-4 mb-8 md:grid-cols-4">
          <div className="p-3 bg-white rounded-lg border border-border">
            <div className="text-2xl font-bold text-gray-900">
              {articles.length}
            </div>
            <div className="text-sm text-gray-600">Total Articles</div>
          </div>
          <div className="p-3 bg-white rounded-lg border border-border">
            <div className="text-2xl font-bold text-green-600">
              {articles.filter((a) => a.status === 'published').length}
            </div>
            <div className="text-sm text-gray-600">Published</div>
          </div>
          <div className="p-3 bg-white rounded-lg border border-border">
            <div className="text-2xl font-bold text-yellow-600">
              {articles.filter((a) => a.status === 'draft').length}
            </div>
            <div className="text-sm text-gray-600">Drafts</div>
          </div>
          <div className="p-3 bg-white rounded-lg border border-border">
            <div className="text-2xl font-bold text-blue-600">
              {
                articles.filter((a) => a.status === 'scheduled-for-publishing')
                  .length
              }
            </div>
            <div className="text-sm text-gray-600">Scheduled</div>
          </div>
        </div> */}

        {/* Controls */}
        <div className="flex flex-col gap-4 mb-6 justify-between sm:flex-row">
          {/* Search */}
          {/* TODO: Implement search */}
          {/* <div className="flex-1 max-w-80">
            <Input
              type="text"
              icon={<SearchIcon />}
              placeholder="Search articles by title or topic..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 max-w-none"
            />
          </div> */}

          <Select
            defaultValue="all"
            onValueChange={(value) =>
              setFilter(value as 'all' | 'published' | 'draft' | 'scheduled')
            }
          >
            <SelectTrigger className="w-[180px]">
              <div className="flex items-center gap-2">
                <FilterIcon />
                <SelectValue placeholder="Filter" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>Filter</SelectLabel>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="published">Published</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>

          {/* Filter */}
          {/* <SlidingButtonGroup<'all' | 'published' | 'draft' | 'scheduled'>
            value={filter}
            onValueChange={(status) => setFilter(status)}
            options={[
              { value: 'all', label: 'All' },
              { value: 'published', label: statusLabels.published },
              { value: 'draft', label: statusLabels.draft },
              { value: 'scheduled', label: statusLabels.scheduled },
            ]}
          /> */}
        </div>

        {/* Articles Grid */}
        <div className="grid overflow-hidden rounded-3xl border border-border bg-card">
          {isPending
            ? // Loading skeleton
              Array.from({ length: 3 }).map((_, i) => (
                <div key={`skeleton-${i}`} className="p-6 animate-pulse">
                  <div className="flex justify-between items-center mb-4">
                    <div className="w-1/3 h-6 bg-gray-200 rounded"></div>
                    <div className="w-24 h-6 bg-gray-200 rounded"></div>
                  </div>
                  <div className="w-2/3 h-4 mb-2 bg-gray-200 rounded"></div>
                  <div className="w-1/2 h-4 bg-gray-200 rounded"></div>
                </div>
              ))
            : articles.length === 0 && (
                <div className="py-12 text-center">
                  <p className="text-lg text-gray-500">
                    No articles found. Create an article to get started.
                  </p>
                </div>
              )}

          {!isPending &&
            articles.length > 0 &&
            articles.map((article) => (
              <React.Fragment key={article.id}>
                <article key={article.id} className="p-6">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    {/* Image */}
                    <Link
                      to="/article/$articleId"
                      params={{ articleId: article.id }}
                      className="md:w-24 md:h-24 w-16 h-16"
                    >
                      {article.coverPhotoUrl ? (
                        <img
                          key={`image-${article.id}`}
                          src={article.coverPhotoUrl}
                          alt={article.title ? article.title : 'Article Image'}
                          // width={300}
                          // height={300}
                          className="object-cover w-full h-full rounded-lg"
                        />
                      ) : (
                        <div className="flex items-center justify-center w-full h-full bg-gray-200 rounded-lg">
                          <span className="text-sm text-gray-500">
                            No Image
                          </span>
                        </div>
                      )}
                    </Link>
                    {/* Article Info */}
                    <div className="flex-1">
                      <div className="flex gap-3 mb-1 ites-start">
                        <Link
                          to="/article/$articleId"
                          params={{ articleId: article.id }}
                        >
                          <h2 className="flex-1 text-xl font-semibold text-gray-900 line-clamp-1">
                            {article.title}
                          </h2>
                        </Link>
                      </div>

                      <p className="mb-2 line-clamp-1 text-muted-foreground">
                        {article.summary}
                      </p>

                      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                        <span className="flex gap-1 items-center">
                          <Tooltip>
                            <TooltipTrigger>
                              <Calendar className="w-4 h-4" />
                            </TooltipTrigger>
                            <TooltipContent>
                              The date this article was written.
                            </TooltipContent>
                          </Tooltip>
                          {formatDate(
                            article.writtenAt?.toISOString() ??
                              article.createdAt.toISOString(),
                          )}
                        </span>
                        {article.keywords && (
                          <span className="flex gap-1 items-center">
                            <Tooltip>
                              <TooltipTrigger>
                                <Tag className="w-4 h-4" />
                              </TooltipTrigger>
                              <TooltipContent>
                                The keywords focused on for this article.
                              </TooltipContent>
                            </Tooltip>
                            <KeywordList keywords={article.keywords} />
                          </span>
                        )}
                        {/* <span className="flex gap-1 items-center">
                          <FileText className="w-4 h-4" />
                          {article.content?.length} words
                        </span> */}
                      </div>
                    </div>

                    <div>
                      <ArticleStatusBadge
                        className="w-fit"
                        status={article.status}
                        scheduledDate={article.scheduledDate}
                      />
                    </div>
                  </div>
                </article>
                <div
                  key={`divider-${article.id}`}
                  className="mx-4 h-px bg-border/60 last:hidden"
                />
              </React.Fragment>
            ))}
        </div>
      </div>
    </div>
  )
}

function KeywordList({ keywords }: { keywords: string[] }) {
  const truncatedKeywords = keywords.slice(0, 1)
  const remainingKeywords = keywords.slice(1)
  const hasRemainingKeywords = remainingKeywords.length > 0

  return (
    <div className="flex flex-wrap gap-1">
      {truncatedKeywords.map((keyword, index) => (
        <span key={keyword} className="text-sm text-muted-foreground">
          {keyword}
          {index < truncatedKeywords.length - 1 && ','}
        </span>
      ))}
      {hasRemainingKeywords && (
        <Tooltip>
          <TooltipTrigger className="ml-1">
            <Badge variant="secondary">{`+${remainingKeywords.length} more`}</Badge>
          </TooltipTrigger>
          <TooltipContent>{remainingKeywords.join(', ')}</TooltipContent>
        </Tooltip>
      )}
    </div>
  )
}
